import { useEffect, useState } from "react";
import { Icons } from "../components/Icons.js";
import { api, ApiError, type SummaryItem } from "../lib/api.js";
import { summaryDone, summaryFailed, summaryStarted } from "../components/SummaryToast.js";
import { fmtLocalDate } from "../lib/format.js";
import type { EditTarget } from "./types.js";

type Filter = "tous" | "jour" | "semaine" | "autonome";

function family(t: SummaryItem["targetType"]): Filter {
  if (t === "day") return "jour";
  if (t === "standalone") return "autonome";
  return "semaine";
}
function title(s: SummaryItem): string {
  if (s.targetType === "day") return s.targetDate ? fmtLocalDate(s.targetDate) : "Jour";
  if (s.targetType === "standalone") return "Note";
  return `Semaine ${s.isoWeek ?? "?"}`;
}
function meta(s: SummaryItem): string {
  if (s.targetType === "day") return `Journée · résumé ${s.seq}`;
  if (s.targetType === "standalone") return `Note · résumé ${s.seq}`;
  return `${s.isPartial ? "Partiel" : "Hebdomadaire"} · ${s.isoYear ?? ""}`;
}

/**
 * Comptes rendus (§9 / écran Résumés). Liste des résumés actifs ; chaque carte ouvre la fenêtre
 * d'édition (afficher / éditer / copier / e-mail) et offre régénérer et supprimer (corbeille). Une
 * barre permet de générer le résumé du jour ou de la semaine à la demande.
 */
export function ResumesView({ onOpen, reloadToken }: { onOpen: (t: EditTarget) => void; reloadToken: number }) {
  const [filter, setFilter] = useState<Filter>("tous");
  const [items, setItems] = useState<SummaryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .listSummaries()
      .then((rows) => !cancelled && setItems(rows))
      .catch(() => undefined)
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [reloadToken, tick]);

  const refresh = () => setTick((t) => t + 1);
  const act = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    setMsg(null);
    try {
      await fn();
      setMsg(ok);
      refresh();
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : "Action impossible.");
    } finally {
      setBusy(false);
    }
  };

  /**
   * Génération à la demande. L'attente (appel au modèle) et le résultat sont portés par le bandeau
   * global : il survit au changement d'écran et permet d'ouvrir le résumé produit d'un appui.
   */
  const gen = async (label: string, fn: () => Promise<{ summaryId: string }>) => {
    setBusy(true);
    setMsg(null);
    summaryStarted(label);
    try {
      const res = await fn();
      summaryDone(label, res.summaryId);
      refresh();
    } catch (e) {
      summaryFailed(e instanceof ApiError ? e.message : "Génération impossible.");
    } finally {
      setBusy(false);
    }
  };

  const shown = items.filter((s) => filter === "tous" || family(s.targetType) === filter);

  return (
    <>
      <div className="filterbar">
        {([["tous", "Tous"], ["jour", "Jour"], ["semaine", "Semaine"]] as [Filter, string][]).map(([k, l]) => (
          <button key={k} className={"chip" + (filter === k ? " chip-on" : "")} onClick={() => setFilter(k)}>
            {l}
          </button>
        ))}
      </div>
      <div className="editwin-actions" style={{ border: "none", paddingTop: 0 }}>
        <button className="ewbtn" disabled={busy} onClick={() => gen("Résumé du jour", () => api.generateDaySummary())}>
          <Icons.Doc size={16} /> Résumé du jour
        </button>
        <button className="ewbtn" disabled={busy} onClick={() => gen("Résumé de semaine", () => api.generateWeekSummary(undefined, undefined, false))}>
          <Icons.Calendar size={16} /> Résumé de semaine
        </button>
        {msg && <span className="editwin-flash">{msg}</span>}
      </div>
      {loading ? (
        <div className="listspinner"><div className="spinner" /></div>
      ) : shown.length === 0 ? (
        <div className="empty">
          <div className="empty-ic"><Icons.Doc size={28} /></div>
          <div className="empty-tt">Aucun compte rendu</div>
          <div className="empty-sub">Les résumés générés apparaîtront ici.</div>
        </div>
      ) : (
        <div className="resumelist">
          {shown.map((s) => {
            const isWeek = family(s.targetType) === "semaine";
            return (
              <div key={s.id} className="rcard">
                <div className="rcard-head" onClick={() => onOpen({ kind: "summary", id: s.id, label: title(s) })}>
                  <span className={"rcard-ic " + (isWeek ? "rcard-ic-week" : "rcard-ic-day")}>
                    {isWeek ? <Icons.Calendar size={20} /> : s.targetType === "standalone" ? <Icons.Mic size={20} /> : <Icons.Doc size={20} />}
                  </span>
                  <span className="rcard-tt">
                    <span className="rcard-title">
                      {title(s)}
                      {s.isPartial && <span className="rtag">partiel</span>}
                    </span>
                    <span className="rcard-meta">{meta(s)}</span>
                  </span>
                  {/* Pas de bouton « régénérer » ici : régénérer depuis la liste produisait un
                      résumé de plus sans qu'on l'ait demandé explicitement. La génération se lance
                      depuis la barre ci-dessus (jour / semaine) ou depuis la note elle-même. */}
                  <span className="rcard-actions" onClick={(e) => e.stopPropagation()}>
                    <button className="ricon ricon-trash" disabled={busy} onClick={() => act(() => api.deleteSummary(s.id), "Résumé mis à la corbeille.")} aria-label="Supprimer" title="Supprimer">
                      <Icons.Trash size={18} />
                    </button>
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
